import { useMemo } from "react";
import { Flame, ArrowUp, ArrowDown } from "lucide-react";

const shortMoney = (n) => {
  if (n == null) return "—";
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(n >= 10_000_000 ? 0 : 1).replace(/\.0$/, "")}M`;
  if (n >= 1_000) return `$${Math.round(n / 1_000)}K`;
  return `$${n}`;
};

const medianOf = (arr) => {
  const s = [...arr].filter((n) => typeof n === "number").sort((a, b) => a - b);
  if (!s.length) return null;
  const m = Math.floor(s.length / 2);
  return s.length % 2 ? s[m] : Math.round((s[m - 1] + s[m]) / 2);
};

// Dark-slate → luxe-gold ramp, scaled by where the median sits in [min, max].
function heat(t) {
  const clamped = Math.max(0, Math.min(1, t));
  const sat = 18 + clamped * 62;   // 18% → 80%
  const light = 20 + clamped * 26; // 20% → 46%
  return `hsl(40, ${sat}%, ${light}%)`;
}

// Generic median-price heat strip. `tiles`: [{ key, label, median, count }].
export default function HeatStrip({ tiles = [], min, max, loading, activeKey, onSelect, title, unit = "listing", testidPrefix = "heat" }) {
  const items = useMemo(() => [...tiles].sort((a, b) => a.median - b.median), [tiles]);
  const overallMedian = useMemo(() => medianOf(items.map((i) => i.median)), [items]);

  if (loading) return <div data-testid={`${testidPrefix}-loading`} className="mb-10 h-24 rounded-xl border border-border bg-surface animate-pulse" />;
  if (!items.length) return null;

  const span = (max ?? 0) - (min ?? 0) || 1;

  return (
    <div className="mb-10" data-testid={`${testidPrefix}-strip`}>
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Flame className="w-4 h-4 text-accent" />
          <span className="font-mono-data text-[11px] uppercase tracking-wider text-muted-foreground">{title}</span>
          <span className="hidden sm:inline font-mono-data text-[9px] uppercase tracking-wider text-muted-foreground/60 ml-1">
            <ArrowUp className="w-2.5 h-2.5 inline text-amber-400" /> premium · <ArrowDown className="w-2.5 h-2.5 inline text-emerald-400" /> value vs median
          </span>
        </div>
        <div className="flex items-center gap-2 font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground/70">
          <span>{shortMoney(min)}</span>
          <span className="h-2 w-24 rounded-full" style={{ background: `linear-gradient(90deg, ${heat(0)}, ${heat(0.5)}, ${heat(1)})` }} />
          <span>{shortMoney(max)}</span>
        </div>
      </div>
      <div className="flex gap-2 overflow-x-auto pb-1" data-testid={`${testidPrefix}-tiles`}>
        {items.map((z) => {
          const t = (z.median - (min ?? 0)) / span;
          const active = String(activeKey) === String(z.key);
          const pct = overallMedian ? Math.round(((z.median - overallMedian) / overallMedian) * 100) : 0;
          const up = pct > 0;
          return (
            <button
              key={z.key}
              onClick={() => onSelect?.(z.key)}
              data-testid={`${testidPrefix}-tile-${z.key}`}
              title={`${z.label} · median ${shortMoney(z.median)} · ${z.count} ${unit}${z.count === 1 ? "" : "s"} · ${pct > 0 ? "+" : ""}${pct}% vs median`}
              className={`shrink-0 min-w-[104px] rounded-xl px-3 py-2.5 text-left transition-all duration-200 border ${active ? "border-accent ring-2 ring-accent/40 scale-[1.03]" : "border-white/10 hover:border-white/30 hover:-translate-y-0.5"}`}
              style={{ backgroundColor: heat(t) }}
            >
              <div className="font-mono-data text-[11px] tracking-wider text-white/90 truncate max-w-[120px]">{z.label}</div>
              <div className="flex items-center gap-1.5 mt-1">
                <span className="text-sm font-semibold text-white leading-tight">{shortMoney(z.median)}</span>
                {pct !== 0 && (
                  <span data-testid={`${testidPrefix}-trend-${z.key}`}
                    className={`inline-flex items-center gap-0.5 rounded px-1 py-0.5 text-[9px] font-mono-data font-semibold bg-black/25 ${up ? "text-amber-300" : "text-emerald-300"}`}>
                    {up ? <ArrowUp className="w-2.5 h-2.5" /> : <ArrowDown className="w-2.5 h-2.5" />}{Math.abs(pct)}%
                  </span>
                )}
              </div>
              <div className="font-mono-data text-[9px] uppercase tracking-wider text-white/60 mt-0.5">{z.count} {unit}{z.count === 1 ? "" : "s"}</div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
